/**
 * Storage, grammar, and matching for the rebindable shortcuts catalogued in
 * `shortcutCatalog.ts`.
 *
 * Split from the catalog so the dependency runs one way — catalog knows nothing
 * about storage, this file reads the catalog for its defaults. That is what lets
 * a new shortcut be added by editing one array.
 *
 * ## Grammar
 *
 * A binding is a lowercase `+`-joined chord: `mod+k`, `mod+shift+b`. `mod` is the
 * platform's primary modifier — ⌘ on Apple, Ctrl elsewhere — so ONE stored string
 * is correct on both platforms and a user who moves machines does not find their
 * bindings meaningless. Modifier order is normalised (mod, alt, shift, key) so two
 * spellings of the same chord compare equal, which is what makes conflict
 * detection a string comparison rather than a set intersection.
 *
 * ## Storage
 *
 * localStorage, matching the sidebar display mode next door in `displayMode.ts`,
 * and for the same reason: this is a per-DEVICE preference. A binding chosen on a
 * Mac laptop is not necessarily the right one on a Windows desktop, and there is
 * no server round-trip in the path of a keystroke. Every access is wrapped —
 * localStorage throws outright in Safari private browsing and when site data is
 * blocked, and a thrown read must not take the app down.
 */

import {
  DEFAULT_SHORTCUT_BINDINGS,
  REBINDABLE_SHORTCUTS,
  SHORTCUT_CATALOG,
  type RebindableShortcutKey,
  type ShortcutCatalogEntry,
} from './shortcutCatalog'
import { IS_APPLE_PLATFORM } from './shortcuts'

export type ShortcutBindings = Record<RebindableShortcutKey, string>

export const SHORTCUT_BINDINGS_STORAGE_KEY = 'vera:shortcuts:v1'

/**
 * Fired on the window after any write, so every surface showing a binding
 * re-reads it without a reload. `storage` alone is not enough: the browser does
 * not fire it in the tab that did the writing, which is precisely the tab whose
 * sidebar hint has to update.
 */
export const SHORTCUT_BINDINGS_UPDATED_EVENT = 'vera:shortcuts:updated'

const MODIFIERS = ['mod', 'alt', 'shift'] as const

/**
 * Keys a rebindable shortcut may not claim, and who holds them. Both are bound
 * by hardcoded handlers rather than by stored bindings, so they cannot be caught
 * by comparing against the bindings object — they need naming here or a user
 * could bind ⇧? over the dialog's own opener and lose the way back in.
 */
const RESERVED_KEYS: Partial<Record<string, ShortcutCatalogEntry['key']>> = {
  '?': 'openShortcutsDialog',
  escape: 'dismiss',
}

/** Sort modifiers into a canonical order and lowercase everything, so `Shift+Mod+B`
 *  and `mod+shift+b` are the same string. */
export const normalizeBinding = (raw: string): string => {
  const parts = raw
    .toLowerCase()
    .split('+')
    .map(part => part.trim())
    .filter(Boolean)
    // `meta` / `control` / `ctrl` all mean the platform's primary modifier.
    .map(part => (['meta', 'control', 'ctrl', 'cmd', 'command'].includes(part) ? 'mod' : part))

  const held = new Set(parts.filter(part => (MODIFIERS as readonly string[]).includes(part)))
  const key = parts.find(part => !(MODIFIERS as readonly string[]).includes(part)) ?? ''

  return [...MODIFIERS.filter(modifier => held.has(modifier)), key].filter(Boolean).join('+')
}

/** True when the binding carries at least one modifier. A bare letter would fire
 *  while someone was simply reading the page, so rebinding refuses it. */
export const isChord = (binding: string): boolean =>
  normalizeBinding(binding)
    .split('+')
    .some(part => (MODIFIERS as readonly string[]).includes(part))

/** The key half of a chord — `k` for `mod+k`. Empty when the chord is modifiers only. */
export const bindingKey = (binding: string): string =>
  normalizeBinding(binding)
    .split('+')
    .find(part => !(MODIFIERS as readonly string[]).includes(part)) ?? ''

/**
 * Turn a keydown into a binding string, or null when the event carries no key of
 * its own (a lone ⌘ press while the user is still reaching for the second key).
 */
export const captureKeydownToBinding = (event: KeyboardEvent): string | null => {
  const pressed = (event.key ?? '').toLowerCase()
  if (!pressed || ['meta', 'control', 'shift', 'alt', 'os', 'dead'].includes(pressed)) return null

  const parts: string[] = []
  if (event.metaKey || event.ctrlKey) parts.push('mod')
  if (event.altKey) parts.push('alt')
  if (event.shiftKey) parts.push('shift')
  parts.push(pressed === ' ' ? 'space' : pressed)

  return normalizeBinding(parts.join('+'))
}

/**
 * Render a binding for display: `⌘K` / `⇧⌘K` on Apple, `Ctrl K` / `Ctrl Shift K`
 * elsewhere — matching the `formatShortcut` house style already used by the
 * sidebar rows, so a chord and a single-key hint look like the same system.
 *
 * A non-chord (a fixed entry's `?` or `Esc`) passes through untouched: those are
 * display tokens, not chords.
 */
export const formatBinding = (binding: string): string => {
  if (!isChord(binding)) return binding

  const parts = normalizeBinding(binding).split('+')
  const rendered = parts.map(part => {
    if (part === 'mod') return IS_APPLE_PLATFORM ? '⌘' : 'Ctrl'
    if (part === 'shift') return IS_APPLE_PLATFORM ? '⇧' : 'Shift'
    if (part === 'alt') return IS_APPLE_PLATFORM ? '⌥' : 'Alt'
    if (part === 'space') return 'Space'
    return part.length === 1 ? part.toUpperCase() : part.charAt(0).toUpperCase() + part.slice(1)
  })

  // Apple convention prints modifiers flush against the key (⇧⌘K); Windows and
  // Linux spell them out, which needs separators to stay readable.
  return IS_APPLE_PLATFORM ? rendered.join('') : rendered.join(' ')
}

/** The `aria-keyshortcuts` spelling of a chord, which wants the literal modifier
 *  names and `+` separators regardless of platform. */
export const ariaBinding = (binding: string): string =>
  normalizeBinding(binding)
    .split('+')
    .map(part => {
      if (part === 'mod') return IS_APPLE_PLATFORM ? 'Meta' : 'Control'
      if (part === 'shift') return 'Shift'
      if (part === 'alt') return 'Alt'
      return part.toUpperCase()
    })
    .join('+')

/** True when this keydown IS the given binding. `event.repeat` is excluded so
 *  holding the chord fires once rather than autorepeating a toggle. */
export const eventMatchesBinding = (event: KeyboardEvent, binding: string): boolean => {
  if (event.repeat) return false

  const parts = normalizeBinding(binding).split('+')
  const wantsMod = parts.includes('mod')
  const wantsAlt = parts.includes('alt')
  const wantsShift = parts.includes('shift')

  // Deliberately accepts EITHER ⌘ or Ctrl for `mod` rather than only the
  // platform's own: the platform sniff is a heuristic, and a false negative here
  // means a shortcut that silently does nothing.
  const modHeld = event.metaKey || event.ctrlKey
  if (wantsMod !== modHeld) return false
  if (wantsAlt !== event.altKey) return false
  if (wantsShift !== event.shiftKey) return false

  const pressed = (event.key ?? '').toLowerCase()
  return (pressed === ' ' ? 'space' : pressed) === bindingKey(binding)
}

const isRebindableKey = (value: unknown): value is RebindableShortcutKey =>
  typeof value === 'string' && REBINDABLE_SHORTCUTS.some(entry => entry.key === value)

/**
 * Read the stored bindings, falling back per-key to the catalog default. Per-KEY
 * rather than all-or-nothing so adding a new shortcut does not discard the
 * bindings a user already set for the existing ones.
 *
 * A stored value that is no longer a valid chord (hand-edited storage, or a
 * grammar change) is dropped in favour of the default rather than trusted.
 */
export const loadShortcutBindings = (): ShortcutBindings => {
  const bindings = { ...DEFAULT_SHORTCUT_BINDINGS }
  if (typeof window === 'undefined') return bindings

  try {
    const raw = window.localStorage.getItem(SHORTCUT_BINDINGS_STORAGE_KEY)
    if (!raw) return bindings

    const parsed = JSON.parse(raw) as Record<string, unknown>
    for (const [key, value] of Object.entries(parsed)) {
      if (!isRebindableKey(key)) continue
      if (typeof value !== 'string' || !value.trim()) continue
      const normalized = normalizeBinding(value)
      if (!isChord(normalized)) continue
      bindings[key] = normalized
    }
  } catch {
    // Unreadable storage — the defaults above are already correct.
  }

  return bindings
}

const writeShortcutBindings = (bindings: ShortcutBindings) => {
  if (typeof window === 'undefined') return
  try {
    window.localStorage.setItem(SHORTCUT_BINDINGS_STORAGE_KEY, JSON.stringify(bindings))
  } catch {
    // Storage blocked. The in-memory value still applies for this session.
  }
  window.dispatchEvent(new Event(SHORTCUT_BINDINGS_UPDATED_EVENT))
}

/** Persist one binding and notify every listening surface. Returns what was stored. */
export const saveShortcutBinding = (key: RebindableShortcutKey, binding: string): ShortcutBindings => {
  const next = { ...loadShortcutBindings(), [key]: normalizeBinding(binding) }
  writeShortcutBindings(next)
  return next
}

/** Restore one binding to its catalog default. */
export const resetShortcutBinding = (key: RebindableShortcutKey): ShortcutBindings => {
  const next = { ...loadShortcutBindings(), [key]: DEFAULT_SHORTCUT_BINDINGS[key] }
  writeShortcutBindings(next)
  return next
}

/**
 * The catalog entry a candidate chord would collide with, or null when it is free.
 *
 * Two actions on one binding is not a cosmetic problem: whichever handler is
 * registered first wins and the other silently stops working, with nothing on
 * screen to explain it. So the capture refuses rather than warns.
 *
 * Checked against BOTH the live bindings of the other rebindable entries and the
 * reserved keys held by hardcoded handlers (`?`, Esc) — the latter would otherwise
 * be invisible to a bindings-object comparison.
 *
 * NOTE ON PROVENANCE: this is the one part of the port that is not a port.
 * Vergence's controls card has no conflict detection — its recorder writes any
 * valid chord straight to storage, so two of its shortcuts can and do land on the
 * same keys. Written here rather than copied.
 */
export const findBindingConflict = (
  candidate: string,
  forKey: RebindableShortcutKey,
  bindings: ShortcutBindings,
): ShortcutCatalogEntry | null => {
  const normalized = normalizeBinding(candidate)

  const reservedBy = RESERVED_KEYS[bindingKey(normalized)]
  if (reservedBy) return SHORTCUT_CATALOG.find(entry => entry.key === reservedBy) ?? null

  const clash = REBINDABLE_SHORTCUTS.find(
    entry => entry.key !== forKey && normalizeBinding(bindings[entry.key]) === normalized,
  )

  return clash ?? null
}
